import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LangChangeEvent, TranslateService, TranslationChangeEvent } from '@ngx-translate/core';
import { registerLibTranslations, resolveCoreTranslateLang } from 'core';
import { ReplaySubject, Subject } from 'rxjs';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { ArkikSelectorService } from '@shared/services/arkik-selector.service';
import { MsalService } from '@azure/msal-angular';
import { titleCaseFormat } from '@shared/pipes/title-case-format.util';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { ICompanyModel } from '@core/models/admin/company/company.model';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  public static translation: Map<string, string> = new Map<string, string>();
  public static language = '';

  public static PRODUCT_PATH = 'PRODUCT_PATH';
  public static USE_TRANSLATION_SERVER = 'USE_TRANSLATION_SERVER';

  public labels: Record<string, string> = {};
  public showGroupNaming: boolean;

  private productPath = '';
  private _localeData: Subject<Record<string, string>> = new Subject<Record<string, string>>();
  private update = new ReplaySubject<Date>(1);
  public detectChange = this.update.asObservable();
  private _useTranslationServer = false;
  private registeredLibraries: Record<string, object>[] = [];

  public static st(textID: string): string {
    const textValue = this.translation.get(textID);
    if (!textValue) {
      return 'NOT:' + textID;
    }

    return textValue;
  }

  public static all(): Map<string, string> {
    return this.translation;
  }

  constructor(
    private http: HttpClient,
    @Inject(TranslationService.PRODUCT_PATH)
    @Optional()
    private _productPath: string,
    @Inject(TranslationService.USE_TRANSLATION_SERVER)
    @Optional()
    private useTranslationServer: boolean,
    private translate: TranslateService,
    private authServiceB2C: MsalService,
    private arkikSelectorService: ArkikSelectorService,
    private idb: ManageIdbService,
  ) {
    const rawPath = _productPath || '';
    this.productPath = rawPath && !rawPath.endsWith('/') ? rawPath + '/' : rawPath;
    this._useTranslationServer = useTranslationServer || false;
    this.bindTranslateEvents();
    this.initLanguage();
    if (this.authServiceB2C.instance.getAllAccounts().length) {
      this.readParameters();
    }
  }

  public get localeData() {
    return this._localeData.asObservable();
  }

  public registerLibraryTranslations(translations: Record<string, object>): void {
    if (!translations) return;

    this.registeredLibraries.push(translations);
    registerLibTranslations(this.translate, translations);

    // Re-aplicar cuando ngx-translate termine de cargar el JSON global por HTTP
    this.translate.onLangChange.subscribe(() => {
      this.registeredLibraries.forEach((libDict) => {
        registerLibTranslations(this.translate, libDict);
      });
    });
  }

  public initLanguage(): void {
    if (this.isRunningOnBrowser()) {
      const comingFromReact =
        sessionStorage.getItem('language') ||
        localStorage.getItem('lang') ||
        (window.navigator && window.navigator.languages && window.navigator.languages[0]) ||
        'en_US';
      let langLocalStorage =
        localStorage.getItem('lang') ||
        (window.navigator && window.navigator.languages && window.navigator.languages[0]);

      if (comingFromReact.indexOf('es') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'es';
        localStorage.setItem('lang', 'es');
      } else if (comingFromReact.indexOf('en') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'en';
        localStorage.setItem('lang', 'en');
      } else if (comingFromReact.indexOf('fr') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'fr';
        localStorage.setItem('lang', 'fr');
      } else if (comingFromReact.indexOf('pl') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'pl';
        localStorage.setItem('lang', 'pl');
      } else if (comingFromReact.indexOf('cs') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'cs';
        localStorage.setItem('lang', 'cs');
      } else if (comingFromReact.indexOf('de') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'de';
        localStorage.setItem('lang', 'de');
      } else {
        langLocalStorage = comingFromReact;
        localStorage.setItem('lang', langLocalStorage);
      }

      this.lang(langLocalStorage ? langLocalStorage : this._useTranslationServer ? 'en_US' : 'en');
      return;
    }

    this.lang(this._useTranslationServer ? 'en_US' : 'en');
  }

  public isRunningOnBrowser(): boolean {
    return true;
  }

  public onChange(value: Record<string, string>): void {
    this.labels = value;
    this._localeData.next(value);
    this.update.next(new Date());
  }

  public lang(lang: string): void {
    const normalizedLang = this.normalizeLanguageKey(lang);
    TranslationService.language = normalizedLang;
    this.translate.setDefaultLang(normalizedLang);
    sessionStorage.setItem('lang', this.toSessionLang(normalizedLang));
    this.translate.use(normalizedLang).subscribe((translations) => this.refreshSnapshot(normalizedLang, translations));
  }

  public isoLang(lang: string): void {
    this.lang(lang);
  }

  public file(url: string): void {
    const resourceUrl =
      this.productPath && !/^https?:/i.test(url) && !url.startsWith('./') ? `./${this.productPath}${url}` : url;

    this.http.get<Record<string, unknown>>(resourceUrl).subscribe((response) => {
      this.translate.setTranslation(this.getActiveLanguage(), response, true);
      this.refreshSnapshot();
    });
  }

  public pt(textID: string, args?: any[]): string {
    const resolvedTextId = this.resolveGroupNamingKey(textID, args);
    const translation = this.translate.instant(resolvedTextId, args as any);

    if (typeof translation === 'string' && translation && translation !== resolvedTextId) {
      return translation;
    }

    const snapshotValue = TranslationService.translation.get(resolvedTextId);
    if (snapshotValue) {
      return this.replaceArguments(snapshotValue, args);
    }

    return 'NOT:' + resolvedTextId;
  }

  public npt(textID: string, args?: any[]): string {
    const resolvedTextId = this.resolveGroupNamingKey(textID, args);
    const translation = this.translate.instant(resolvedTextId, args as any);

    if (typeof translation === 'string' && translation && translation !== resolvedTextId) {
      return translation;
    }

    const snapshotValue = TranslationService.translation.get(resolvedTextId);
    return snapshotValue ? this.replaceArguments(snapshotValue, args) : null;
  }

  public getLabel(textId: string): string {
    const translation = this.translate.instant(textId);

    if (typeof translation === 'string' && translation && translation !== textId) {
      return translation;
    }

    return TranslationService.translation.get(textId);
  }

  public getLang(): string {
    return TranslationService.language || this.getActiveLanguage();
  }

  public getLocale(): string {
    switch (this.getActiveLanguage()) {
      case 'es':
        return 'es-MX';
      default:
        return 'en-US';
    }
  }

  public formatString(template: string, ...values: any[]): string {
    const usePipe = typeof values[values.length - 1] === 'boolean' ? values.pop() : false;
    const baseText = template.indexOf('views.') != -1 ? this.pt(template) : template;

    if (!baseText) {
      return 'NOT:' + template;
    }

    const translatedText = baseText.replace(/{(\d+)}/g, (match, index) => values[index] ?? match);
    return usePipe ? titleCaseFormat(translatedText) : translatedText;
  }

  private bindTranslateEvents(): void {
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.refreshSnapshot(event.lang, event.translations as Record<string, unknown>);
    });

    this.translate.onTranslationChange.subscribe((event: TranslationChangeEvent) => {
      if (this.normalizeLanguageKey(event.lang) === this.getActiveLanguage()) {
        this.refreshSnapshot(event.lang, event.translations as Record<string, unknown>);
      }
    });
  }

  private getActiveLanguage(): string {
    return resolveCoreTranslateLang(
      TranslationService.language ||
        this.translate.currentLang ||
        this.translate.defaultLang ||
        localStorage.getItem('lang') ||
        sessionStorage.getItem('language') ||
        sessionStorage.getItem('lang'),
    );
  }

  private resolveGroupNamingKey(textID: string, args?: any[]): string {
    if (args?.length > 0 && args[0] == 'groupName' && !this.showGroupNaming) {
      switch (textID) {
        case 'views.globals.groups':
        case 'views.globals.group':
          return 'views.globals.area';
        case 'views.globals.subgroups':
        case 'views.globals.subgroup':
          return 'views.globals.market';
        case 'views.dispatcherConsole.tracking.subgroupRequired':
          return 'views.dispatcherConsole.tracking.marketRequired';
        case 'views.dispatcherConsole.tracking.groupRequired':
          return 'views.dispatcherConsole.tracking.areaRequired';
      }
    }

    return textID;
  }

  private normalizeLanguageKey(language: string): string {
    return resolveCoreTranslateLang(language || 'en');
  }

  private flattenTranslations(
    translations: Record<string, any>,
    prefix = '',
    target = new Map<string, string>(),
  ): Map<string, string> {
    if (!translations) {
      return target;
    }

    for (const [key, value] of Object.entries(translations)) {
      const translationKey = prefix ? `${prefix}.${key}` : key;

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        this.flattenTranslations(value, translationKey, target);
        continue;
      }

      if (value !== undefined && value !== null) {
        target.set(translationKey, String(value));
      }
    }

    return target;
  }

  private mapToObject(translations: Map<string, string>): Record<string, string> {
    const plainTranslations: Record<string, string> = {};

    translations.forEach((value, key) => {
      plainTranslations[key] = value;
    });

    return plainTranslations;
  }

  private refreshSnapshot(lang = this.getActiveLanguage(), translations?: Record<string, unknown>): void {
    TranslationService.language = this.normalizeLanguageKey(lang);
    TranslationService.translation = this.flattenTranslations((translations as Record<string, any>) || {});
    this.onChange(this.mapToObject(TranslationService.translation));
  }

  private toSessionLang(lang: string): string {
    switch (lang) {
      case 'en':
        return 'en_US';
      case 'es':
        return 'es_MX';
      case 'fr':
        return 'fr_FR';
      default:
        return lang;
    }
  }

  private async readParameters(): Promise<void> {
    const companyInfo = (await this.idb.select(IdbCatalogName.CompanyInfo)) as ICompanyModel;
    if (companyInfo?.NvcCode === '000') {
      return;
    }

    this.showGroupNaming = !!(await this.arkikSelectorService.getParameter('EDIT_MASTER_DATA')).parameterValue;
  }

  private replaceArguments(translation: string, args?: any[]): string {
    if (args?.length) {
      args = args.filter((el) => !(el instanceof Date)).filter((el) => el !== undefined);
      let tokenSearch: RegExpExecArray;
      while ((tokenSearch = /{\d+}/g.exec(translation))) {
        const token = tokenSearch[0];
        const replacement = args[Number(token.replace(/[}{]/g, ''))] ?? '{?}';
        translation = translation.replace(token, replacement);
      }
    }

    return translation;
  }
}
